import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { webLightTheme, webDarkTheme, Theme } from '@fluentui/react-components';

// Override the dialog/backdrop overlay color globally. Sourced from
// `--global-header-bg` (#305367 = rgb 48, 83, 103) in `styles/layout.css` so
// the dim behind modals matches the global top bar exactly.
const TWM_OVERLAY_LIGHT = 'rgba(64, 82, 94, 0.7)';
const TWM_OVERLAY_DARK = 'rgba(64, 82, 94, 0.8)';

// Single lever for the admin app's base UI font. Every Fluent component reads
// tokens.fontFamilyBase, so overriding it here cascades app-wide in both light
// and dark. Inter is loaded via the <link> in index.html; the fallbacks keep
// metrics close to Segoe UI so layouts don't shift if the webfont is slow.
const TWM_FONT_FAMILY =
  "'Inter', 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif";

export const twmLightTheme: Theme = {
  ...webLightTheme,
  colorBackgroundOverlay: TWM_OVERLAY_LIGHT,
  fontFamilyBase: TWM_FONT_FAMILY,
};

export const twmDarkTheme: Theme = {
  ...webDarkTheme,
  colorBackgroundOverlay: TWM_OVERLAY_DARK,
  fontFamilyBase: TWM_FONT_FAMILY,
};

// What the user explicitly chose. 'system' tracks the OS preference live.
export type ThemePreference = 'system' | 'light' | 'dark';
// The resolved theme actually applied to the UI.
type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  themePreference: ThemePreference;
  themeMode: ThemeMode;
  theme: Theme;
  setThemePreference: (pref: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = 'twm-theme-mode';

const getSystemMode = (): ThemeMode =>
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
    // No stored choice → follow the OS by default.
    return 'system';
  });

  // Live OS preference, kept current so 'system' resolves correctly.
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);

  // Persist the user's explicit choice.
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themePreference);
  }, [themePreference]);

  // Track OS preference changes regardless of the current selection, so
  // 'system' always reflects the OS (even after toggling away and back).
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemMode(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const themeMode: ThemeMode = themePreference === 'system' ? systemMode : themePreference;

  // Sync the resolved theme to the DOM for CSS targeting.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
    document.body.classList.toggle('dark-mode', themeMode === 'dark');
  }, [themeMode]);

  const setThemePreference = (pref: ThemePreference) => {
    setThemePreferenceState(pref);
  };

  const theme = themeMode === 'dark' ? twmDarkTheme : twmLightTheme;

  return (
    <ThemeContext.Provider value={{ themePreference, themeMode, theme, setThemePreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
